import { Blockquote, Image, List, Table, Text, Title } from "@mantine/core";
import React from "react";

// Shared react-markdown -> Mantine component mapping used by every FAQ topic's static
// content page, so editable Markdown files render with the app's theme/typography
// instead of unstyled browser defaults. Add element types here as topics need them.
export const faqMarkdownComponents = {
    h2: ({ children }: { children?: React.ReactNode }) => (
        <Title order={4} mt="md" mb={6}>
            {children}
        </Title>
    ),
    p: ({ children }: { children?: React.ReactNode }) => (
        <Text size="sm" mb={8}>
            {children}
        </Text>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
        <List size="sm" spacing={4} mb={8}>
            {children}
        </List>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
        <List type="ordered" size="sm" spacing={4} mb={8}>
            {children}
        </List>
    ),
    li: ({ children }: { children?: React.ReactNode }) => <List.Item>{children}</List.Item>,
    // Brand-colored quote callout for "> ..." Markdown — real examples, tips, call-to-action
    // asides. No icon: tried one (info circle), removed per feedback — plain brand-colored
    // quote reads better and is simpler to reuse as-is in future topics.
    blockquote: ({ children }: { children?: React.ReactNode }) => (
        <Blockquote color="brand" mt="lg" mb={4} p="sm">
            {children}
        </Blockquote>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
        <Table withTableBorder withColumnBorders mb={8} striped>
            {children}
        </Table>
    ),
    thead: ({ children }: { children?: React.ReactNode }) => <Table.Thead>{children}</Table.Thead>,
    tbody: ({ children }: { children?: React.ReactNode }) => <Table.Tbody>{children}</Table.Tbody>,
    tr: ({ children }: { children?: React.ReactNode }) => <Table.Tr>{children}</Table.Tr>,
    th: ({ children }: { children?: React.ReactNode }) => <Table.Th>{children}</Table.Th>,
    td: ({ children }: { children?: React.ReactNode }) => <Table.Td>{children}</Table.Td>,
    // Screenshots embedded in FAQ content (e.g. search-overview.*.md) — click to open full size,
    // since there's no in-app lightbox/zoom yet.
    img: ({ src, alt }: { src?: string; alt?: string }) => (
        <a href={src} target="_blank" rel="noreferrer">
            <Image
                src={src}
                alt={alt}
                radius="md"
                my="sm"
                style={{
                    maxWidth: "100%",
                    border: "1px solid var(--mantine-color-default-border)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
            />
        </a>
    ),
};

